import type { TimelineRunsReportResponse } from "../../types";

type TimelineBar = {
  key: string;
  centerPct: number;
  widthPct: number;
  statusClass: string;
  tooltipLines: string[];
  connectorRunId: number;
};

export type TimelineRow = {
  id: string;
  projectId: string;
  projectName: string;
  connectorType: string;
  functionName: string;
  bars: TimelineBar[];
};

export type CursorState = { leftPx: number; timeMs: number };
export type HoverState = { left: number; top: number; lines: string[]; dotKey: string };
export type AxisMetrics = { leftContentPx: number; widthPx: number };

export type TimelineLayout = {
  projectGroups: Array<{ projectName: string; rows: TimelineRow[] }>;
  minMs: number;
  maxMs: number;
  rangeMs: number;
  hasData: boolean;
  availableDays: number;
  clampedRangeDays: number;
};

type RawBarEntry = Omit<TimelineBar, "centerPct" | "widthPct"> & {
  startedAtMs: number;
  endedAtMs: number;
};
type RawRowEntry = Omit<TimelineRow, "bars" | "functionName"> & {
  functionName: string;
  functionNames: string[];
  bars: RawBarEntry[];
};

const EMPTY_LAYOUT: TimelineLayout = {
  projectGroups: [],
  minMs: 0,
  maxMs: 0,
  rangeMs: 1,
  hasData: false,
  availableDays: 1,
  clampedRangeDays: 1,
};

export function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function getStatusClass(status: string): string {
  switch (status) {
    case "completed":
      return "dot-completed";
    case "failed":
      return "dot-failed";
    case "timed_out":
      return "dot-timeout";
    case "started":
      return "dot-running";
    default:
      return "dot-unknown";
  }
}

function summarizeModeFromTableRuns(tableRuns: Array<{ mode: string | null }>): string {
  const hasIncremental = tableRuns.some((r) => r.mode === "incremental");
  const hasFull = tableRuns.some((r) => r.mode === "full");
  if (!hasIncremental && !hasFull) return "–";
  if (hasIncremental && hasFull) return "incremental  full";
  return hasIncremental ? "incremental" : "full";
}

function buildRawBar(
  projectRun: TimelineRunsReportResponse["runs"][number],
  connectorRun: TimelineRunsReportResponse["runs"][number]["connectorRuns"][number],
  startedAtMs: number,
  endedAtMs: number,
): RawBarEntry {
  const totals = connectorRun.tableRuns.reduce(
    (acc, t) => {
      acc.inserted += t.recordsInserted;
      acc.fetched += t.recordsFetched;
      acc.skipped += t.recordsSkipped;
      return acc;
    },
    { inserted: 0, fetched: 0, skipped: 0 },
  );
  const modeSummary = summarizeModeFromTableRuns(connectorRun.tableRuns);
  const endedLabel = connectorRun.endedAt
    ? formatDateTime(connectorRun.endedAt)
    : `${formatDateTime(new Date(endedAtMs).toISOString())} (derived)`;
  const tableNames = Array.from(new Set(connectorRun.tableRuns.map((t) => t.tableName))).sort(
    (a, b) => a.localeCompare(b),
  );
  const tooltipLines = [
    `Status: ${connectorRun.status}`,
    `Mode: ${modeSummary}`,
    `Started: ${formatDateTime(connectorRun.startedAt)}`,
    `Ended: ${endedLabel}`,
    `Tables: ${tableNames.join("   ")}`,
    `Inserted: ${totals.inserted}`,
    `Fetched: ${totals.fetched}`,
    `Skipped: ${totals.skipped}`,
  ];
  const statusClass =
    connectorRun.status === "completed" && totals.inserted === 0
      ? "dot-completed-noinsert"
      : getStatusClass(connectorRun.status);
  return {
    key: `${projectRun.id}-${connectorRun.id}-${startedAtMs}`,
    statusClass,
    tooltipLines,
    startedAtMs,
    endedAtMs,
    connectorRunId: connectorRun.id,
  };
}

function buildRawRows(
  data: TimelineRunsReportResponse,
  splitByFunction: boolean,
): { rowMap: Map<string, RawRowEntry>; min: number; max: number } {
  const rowMap = new Map<string, RawRowEntry>();
  let min = Number.POSITIVE_INFINITY;
  let max = 0;
  for (const projectRun of data.runs) {
    const functionName =
      projectRun.functionName && projectRun.functionName.trim().length > 0
        ? projectRun.functionName
        : "";
    for (const connectorRun of projectRun.connectorRuns) {
      const connectorType = connectorRun.connector.type;
      const rowKey = splitByFunction
        ? [projectRun.projectId, connectorType, functionName].join("|")
        : [projectRun.projectId, connectorType].join("|");
      let row = rowMap.get(rowKey);
      if (!row) {
        row = {
          id: rowKey,
          projectId: projectRun.projectId,
          projectName: projectRun.project.name,
          connectorType,
          functionName: "",
          functionNames: [],
          bars: [],
        };
        rowMap.set(rowKey, row);
      }
      if (functionName && !row.functionNames.includes(functionName)) {
        row.functionNames.push(functionName);
      }
      const startedAtMs = new Date(connectorRun.startedAt).getTime();
      const childMaxStart = connectorRun.tableRuns.reduce((acc, tr) => {
        const t = new Date(tr.startedAt).getTime();
        return Number.isFinite(t) ? Math.max(acc, t) : acc;
      }, Number.NEGATIVE_INFINITY);
      const endedAtMs = connectorRun.endedAt
        ? new Date(connectorRun.endedAt).getTime()
        : Number.isFinite(childMaxStart)
          ? childMaxStart
          : startedAtMs;
      min = Math.min(min, startedAtMs);
      max = Math.max(max, endedAtMs);
      row.bars.push(buildRawBar(projectRun, connectorRun, startedAtMs, endedAtMs));
    }
  }
  return { rowMap, min, max };
}

function resolveRowFunctionName(names: string[], splitByFunction: boolean): string {
  if (splitByFunction) return names[0] ?? "";
  if (names.length === 0) return "";
  return names.length === 1 ? names[0] : "Multiple";
}

function clampBars(
  rawBars: RawBarEntry[],
  minMs: number,
  maxMs: number,
  rangeMs: number,
): TimelineBar[] {
  return rawBars
    .sort((a, b) => a.startedAtMs - b.startedAtMs)
    .filter(({ startedAtMs, endedAtMs }) => endedAtMs >= minMs && startedAtMs <= maxMs)
    .map(({ startedAtMs, endedAtMs, ...rest }) => {
      const startClamped = Math.max(minMs, startedAtMs);
      const endClamped = Math.min(maxMs, Math.max(startClamped, endedAtMs));
      const startPct = clamp01((startClamped - minMs) / rangeMs) * 100;
      const endPct = clamp01((endClamped - minMs) / rangeMs) * 100;
      const widthPct = Math.max(0, endPct - startPct);
      const centerPct = startPct + widthPct / 2;
      return { ...rest, centerPct, widthPct };
    });
}

function sortRows(rows: TimelineRow[]): void {
  rows.sort((a, b) => {
    const pc = a.projectName.localeCompare(b.projectName);
    if (pc !== 0) return pc;
    const cc = a.connectorType.localeCompare(b.connectorType);
    if (cc !== 0) return cc;
    const aE = a.functionName.trim().length === 0;
    const bE = b.functionName.trim().length === 0;
    if (aE !== bE) return aE ? 1 : -1;
    return a.functionName.localeCompare(b.functionName);
  });
}

function groupRowsByProject(
  rows: TimelineRow[],
): Array<{ projectName: string; rows: TimelineRow[] }> {
  const grouped = new Map<string, TimelineRow[]>();
  for (const row of rows) {
    let group = grouped.get(row.projectName);
    if (!group) {
      group = [];
      grouped.set(row.projectName, group);
    }
    group.push(row);
  }
  return Array.from(grouped.entries()).map(([projectName, rows]) => ({ projectName, rows }));
}

export function computeTimelineLayout(
  data: TimelineRunsReportResponse | null,
  rangeDays: number,
  splitByFunction: boolean,
): TimelineLayout {
  if (!data) return EMPTY_LAYOUT;
  const { rowMap, min, max } = buildRawRows(data, splitByFunction);
  const dataMinMs = Number.isFinite(min) ? min : 0;
  const dataMaxMs = max || dataMinMs;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const availableDays = Math.max(1, Math.min(14, Math.ceil((dataMaxMs - dataMinMs) / DAY_MS) + 1));
  const clampedRangeDays = Math.max(1, Math.min(availableDays, rangeDays));
  const maxMs = dataMaxMs;
  const minMs = maxMs - clampedRangeDays * DAY_MS;
  const rangeMs = Math.max(1, maxMs - minMs);

  for (const r of rowMap.values()) {
    r.functionName = resolveRowFunctionName(r.functionNames, splitByFunction);
  }

  const rows: TimelineRow[] = Array.from(rowMap.values()).map((r) => ({
    id: r.id,
    projectId: r.projectId,
    projectName: r.projectName,
    connectorType: r.connectorType,
    functionName: r.functionName,
    bars: clampBars(r.bars, minMs, maxMs, rangeMs),
  }));

  sortRows(rows);

  return {
    projectGroups: groupRowsByProject(rows),
    minMs,
    maxMs,
    rangeMs,
    hasData: rows.length > 0 && minMs > 0 && maxMs > 0,
    availableDays,
    clampedRangeDays,
  };
}
